/**
 * Traffic avoidance, in two halves sharing this module's maths:
 *
 *  1. The "Avoid traffic" chip on the route preview - picks, from the options
 *     already fetched, the one that dodges the most congestion while staying
 *     reasonable to drive (not a maze of turns, not absurdly longer).
 *  2. The watcher during navigation - every few minutes it re-asks the router
 *     from the live position, finds which fresh route is the one being driven
 *     (by geometric overlap - the router doesn't label it), and when real
 *     congestion sits ahead AND an easier route saves real time, offers the
 *     switch in one tap. It never reroutes on its own: surprise reroutes in
 *     traffic are how drivers end up in a soi arguing with their phone.
 *
 * Mapbox driving-traffic remains the single route and timing contract. The
 * watcher compares its live duration with its typical duration, then asks the
 * driver before changing anything.
 */

import type { LngLat, Route } from '../core/types';
import { distance } from '../core/geo';
import { paceDuration } from './paceModel';
import type { RouteStats } from './routeStats';
import {
  expresswayGeometryProfile,
  FAST_ROAD_DETOUR_S,
  FAST_ROAD_EXTRA_DISTANCE_M,
  FAST_ROAD_GAIN_S,
  fastRoadSeconds,
  viableExpresswayRoutes,
} from './bigRoads';

/** Comparison-only Mapbox driving-traffic duration. */
export function trafficDuration(r: Route): number {
  return r.duration;
}

/** Mapbox live delay vs typical, seconds. */
export function trafficDelay(r: Route): number {
  return r.duration_typical != null ? Math.max(0, r.duration - r.duration_typical) : 0;
}

/** Seconds saved by the alternative in the same Mapbox traffic response. */
export function trafficSavings(stay: Route, alternative: Route): number {
  return trafficDuration(stay) - trafficDuration(alternative);
}

/** Fraction of the route's distance in heavy or severe congestion. */
export function congestedShare(r: Route): number {
  const cong = r.legs[0]?.annotation?.congestion;
  const coords = r.geometry.coordinates as LngLat[];
  if (!cong || cong.length < coords.length - 1) return 0;
  let bad = 0;
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const d = distance(coords[i], coords[i + 1]);
    total += d;
    if (cong[i] === 'heavy' || cong[i] === 'severe') bad += d;
  }
  return total > 0 ? bad / total : 0;
}

/**
 * The option that avoids the most traffic while staying easy to drive.
 *
 * Candidates more than 35% slower than the quickest are out - "avoids
 * traffic" must not mean "an hour longer". Among the rest, congestion counts
 * double (it is what the button is FOR), and turns and signals stand in for
 * how demanding the drive is. Returns null with fewer than two options.
 */
export function pickLowTraffic(
  routes: Route[],
  stats: Array<RouteStats | undefined>,
): number | null {
  if (routes.length < 2) return null;
  const quickest = Math.min(...routes.map(paceDuration));
  let best = -1;
  let bestScore = Infinity;
  routes.forEach((r, i) => {
    if (paceDuration(r) > quickest * 1.35) return;
    const st = stats[i];
    const score =
      trafficDelay(r) * 2 +
      congestedShare(r) * 1800 +
      (st?.turns ?? 0) * 25 +
      (st?.trafficSignals ?? 0) * 40;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  });
  return best >= 0 ? best : null;
}

/** ~25 evenly spread points of the route still ahead of `alongNow`. */
export function sampleRemaining(coords: LngLat[], cum: number[], alongNow: number): LngLat[] {
  let start = cum.findIndex((d) => d >= alongNow + 500);
  if (start < 0) start = Math.max(0, coords.length - 2);
  const rest = coords.slice(start);
  const step = Math.max(1, Math.floor(rest.length / 25));
  const out: LngLat[] = [];
  for (let i = 0; i < rest.length; i += step) out.push(rest[i]);
  return out;
}

/**
 * How much of `samples` lies on route `r` (within 500 m). Used to recognise
 * "the route we are already on" among a fresh batch of alternatives.
 */
export function overlapShare(samples: LngLat[], r: Route): number {
  if (!samples.length) return 0;
  const coords = r.geometry.coordinates as LngLat[];
  const step = Math.max(1, Math.floor(coords.length / 400));
  let hit = 0;
  for (const p of samples) {
    for (let i = 0; i < coords.length; i += step) {
      if (distance(p, coords[i]) < 500) {
        hit++;
        break;
      }
    }
  }
  return hit / samples.length;
}

export interface TrafficAdvice {
  /** The route being driven, as freshly re-fetched (live traffic applied). */
  stay: Route;
  /** The easier, faster alternative worth offering. */
  alternative: Route;
  /** Seconds the alternative saves over staying. */
  savesSeconds: number;
  /** The rule that made this worth interrupting the driver for. */
  reason: 'traffic' | 'fast-roads';
  /** Current route's live-vs-static delay when traffic caused the offer. */
  delaySeconds: number;
}

/**
 * Decide whether fresh alternatives justify interrupting the driver.
 *
 * Rules, in order: we must be able to recognise the current route among the
 * fresh ones (≥60% overlap - otherwise we would compare against a stranger);
 * its Mapbox live-vs-typical timing must show at least 5 minutes of delay; the
 * alternative must be fairly easy (no more than 10 extra turns) and save at
 * least 4 minutes by the same live timing. When the current route is on
 * expressway, a surface-road/soi detour is never offered as a traffic rescue.
 * The existing fast-road fallback may still offer a higher-speed route inside
 * the driver's 15 km / 15 minute limits when live traffic is inconclusive.
 */
export function adviseOnTraffic(
  fresh: Route[],
  currentSamples: LngLat[],
  currentStats: (r: Route) => RouteStats,
): TrafficAdvice | null {
  if (fresh.length < 2) return null;

  let stay: Route | null = null;
  let stayOverlap = 0;
  for (const r of fresh) {
    const ov = overlapShare(currentSamples, r);
    if (ov > stayOverlap) {
      stayOverlap = ov;
      stay = r;
    }
  }
  if (!stay || stayOverlap < 0.6) return null;

  const stayStats = currentStats(stay);
  const stayDelay = trafficDelay(stay);
  const trafficAhead = stayDelay >= 300;
  if (trafficAhead) {
    const viableExpressways = new Set(viableExpresswayRoutes(fresh));
    const stayUsesExpressway = expresswayGeometryProfile(stay) !== null;
    let best: { route: Route; save: number; expressway: boolean } | null = null;
    for (const r of fresh) {
      if (r === stay) continue;
      const save = trafficSavings(stay, r);
      if (save < 240) continue;
      if (currentStats(r).turns > stayStats.turns + 10) continue; // a maze is no rescue
      const expressway = viableExpressways.has(r);
      if (stayUsesExpressway && !expressway) continue;
      if (
        !best ||
        (expressway && !best.expressway) ||
        (expressway === best.expressway && save > best.save)
      ) {
        best = { route: r, save, expressway };
      }
    }
    if (best) {
      return {
        stay,
        alternative: best.route,
        savesSeconds: best.save,
        reason: 'traffic',
        delaySeconds: stayDelay,
      };
    }
  }

  const stayDuration = paceDuration(stay);
  const staySurface = Math.max(0, stay.duration - fastRoadSeconds(stay));
  let bestFast: Route | null = null;
  let bestSurfaceGain = 0;
  for (const r of fresh) {
    if (r === stay) continue;
    if (r.distance > stay.distance + FAST_ROAD_EXTRA_DISTANCE_M) continue;
    if (paceDuration(r) > stayDuration + FAST_ROAD_DETOUR_S) continue;
    if (currentStats(r).turns > stayStats.turns + 10) continue;
    const surfaceGain = staySurface - Math.max(0, r.duration - fastRoadSeconds(r));
    if (surfaceGain < FAST_ROAD_GAIN_S || fastRoadSeconds(r) <= fastRoadSeconds(stay)) continue;
    if (
      surfaceGain > bestSurfaceGain ||
      (surfaceGain === bestSurfaceGain && bestFast && paceDuration(r) < paceDuration(bestFast))
    ) {
      bestFast = r;
      bestSurfaceGain = surfaceGain;
    }
  }
  if (!bestFast) return null;
  return {
    stay,
    alternative: bestFast,
    savesSeconds: stayDuration - paceDuration(bestFast),
    reason: 'fast-roads',
    delaySeconds: 0,
  };
}
